<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Search } from 'lucide-vue-next'
import DemoVideo from '@/components/DemoVideo.vue'
import SiteFooter from '@/components/SiteFooter.vue'
import SiteHeader from '@/components/SiteHeader.vue'
import { demoVideos, type DemoVideo as DemoVideoRecord } from '@/data/videoDemo'

const query = ref('')
const ratio = ref('全部')
const activeVideo = ref<DemoVideoRecord>(demoVideos[0]!)
const ratios = ['全部', '横屏', '竖屏', '方形'] as const
const ratioMap = { 横屏: '16:9', 竖屏: '9:16', 方形: '1:1' } as const
const queueOrder = ['enterprise-robot', 'creative-rider', 'product-studio']

const filteredVideos = computed(() => {
  const keyword = query.value.trim().toLowerCase()
  return demoVideos.filter((video) => {
    const matchesRatio = ratio.value === '全部' || video.ratio === ratioMap[ratio.value as keyof typeof ratioMap]
    const matchesKeyword = !keyword || `${video.title}${video.type}${video.context}`.toLowerCase().includes(keyword)
    return matchesRatio && matchesKeyword
  })
})

const queueVideos = computed(() => {
  const available = filteredVideos.value.filter((video) => video.id !== activeVideo.value.id)
  const ordered = queueOrder
    .map((id) => available.find((video) => video.id === id))
    .filter((video): video is DemoVideoRecord => Boolean(video))
  return [...ordered, ...available.filter((video) => !queueOrder.includes(video.id))].slice(0, 3)
})

const galleryVideos = computed(() => filteredVideos.value.slice(0, 6))

watch(filteredVideos, (videos) => {
  if (videos.length && !videos.some((video) => video.id === activeVideo.value.id)) {
    activeVideo.value = videos[0]!
  }
}, { immediate: true })
</script>

<template>
  <div class="screening-page">
    <SiteHeader />
    <main class="screening-main">
      <div class="screening-shell">
        <header class="screening-toolbar">
          <h1>视频作品</h1>
          <label class="screening-search">
            <Search :size="18" aria-hidden="true" />
            <input v-model="query" type="search" placeholder="搜索作品" aria-label="搜索作品" />
          </label>
          <div class="screening-ratios" aria-label="按画面比例筛选">
            <button
              v-for="item in ratios"
              :key="item"
              type="button"
              :class="{ active: ratio === item }"
              :aria-pressed="ratio === item"
              @click="ratio = item"
            >{{ item }}</button>
          </div>
        </header>

        <template v-if="filteredVideos.length">
          <section class="screening-deck" aria-label="作品放映台">
            <div class="screening-stage">
              <DemoVideo :key="activeVideo.id" :video="activeVideo" mode="hero" :loop="false" />
              <div class="screening-stage-copy" aria-hidden="true">
                <h2>{{ activeVideo.title }}</h2>
                <span>{{ activeVideo.duration }}</span>
              </div>
            </div>

            <aside class="screening-queue" aria-label="待选作品">
              <button
                v-for="video in queueVideos"
                :key="video.id"
                type="button"
                @click="activeVideo = video"
              >
                <span class="screening-queue-media">
                  <img :src="video.poster" :alt="`${video.title} 视频封面`" />
                  <span class="screening-queue-shade" aria-hidden="true"></span>
                  <span class="screening-queue-copy">
                    <strong>{{ video.title }}</strong>
                    <small>{{ video.duration }}</small>
                  </span>
                </span>
              </button>
            </aside>
          </section>

          <section class="screening-gallery" aria-labelledby="screening-gallery-title">
            <h2 id="screening-gallery-title">更多作品</h2>
            <div class="screening-gallery-grid">
              <button
                v-for="video in galleryVideos"
                :key="video.id"
                type="button"
                :class="{ active: activeVideo.id === video.id }"
                :aria-label="`播放 ${video.title}`"
                :aria-pressed="activeVideo.id === video.id"
                @click="activeVideo = video"
              >
                <span class="screening-gallery-media">
                  <img :src="video.poster" :alt="`${video.title} 视频封面`" />
                </span>
                <span class="screening-gallery-copy">
                  <strong>{{ video.title }}</strong>
                  <small>{{ video.duration }}</small>
                </span>
              </button>
            </div>
          </section>
        </template>

        <section v-else class="screening-empty" role="status">
          <h2>没有找到匹配作品</h2>
          <p>更换关键词或画面比例后再试。</p>
        </section>
      </div>
    </main>
    <SiteFooter />
  </div>
</template>
